import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoadingService } from '@services/loading.service';
import { SensorGroupInfo, SensorGroup, standardGetHttpOptions, standardHttpOptions, SensorGroupHistory, SensorGroupHistoryInfo, UserGroup } from '@models';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { of } from 'rxjs';
import { catchError, finalize, map, retry } from 'rxjs/operators';
import { CommonShared } from '../classes/common';
import moment from 'moment';
import { Authorization } from '../classes/authorization';

@Injectable()

export class SensorGroupService {

  public standardHttpOptionsWithUser: any;
  private apiUrl: string;
  private common = new CommonShared();
  public auth = new Authorization();

  // Cached variables
  public sensorGroupWithSensorsAndReadingTypes: SensorGroup[] = [];
  public sensorGroups: SensorGroup[] = [];
  public sensorGroupsWithSensors: SensorGroup[] = [];


  constructor(private http: HttpClient, private loadingService: LoadingService, private userService: UserService,
              @Inject(KIG_SETTINGS) private settings: KigSettings, ) {

    this.apiUrl = this.settings.apiUrl;
  }


  public clearCachedVariables() {
    this.sensorGroupWithSensorsAndReadingTypes = [];
    this.sensorGroups = [];
    this.sensorGroupsWithSensors = [];
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getSensorGroupWithSensorsAndReadingTypes(): Observable<any> {
    if (this.sensorGroupWithSensorsAndReadingTypes.length > 0) {
      //console.log('cached getSensorGroupWithSensorsAndReadingTypes');
      return new Observable<SensorGroup[]>((obs) => {
        obs.next(this.sensorGroupWithSensorsAndReadingTypes);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading Sensor Groups With Sensors and Reading Types...');
    return this.http.get(`${this.apiUrl}/sensorgroup/IncludeAll`, standardGetHttpOptions).pipe(
      map((response: SensorGroup[]) => {
        //console.log('fetched getSensorGroupWithSensorsAndReadingTypes');
        this.sensorGroupWithSensorsAndReadingTypes = response;
        this.sensorGroupWithSensorsAndReadingTypes = this.sensorGroupWithSensorsAndReadingTypes.sort((a, b) => a.name.toString().localeCompare(b.name.toString(), 'en', { numeric: true }));
        return this.sensorGroupWithSensorsAndReadingTypes;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSensorGroups(): Observable<any> {
    if (this.sensorGroups.length > 0) {
      //console.log('cached sensorGroups');
      return new Observable<SensorGroup[]>((obs) => {
        obs.next(this.sensorGroups);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading Sensor Groups...');
    return this.http.get(`${ this.apiUrl }/sensorgroup`, standardGetHttpOptions).pipe(
      map((response: SensorGroup[]) => {
        //console.log('fetched sensorGroups');
        this.sensorGroups = response;
        this.sensorGroups = this.sensorGroups.sort((a, b) => a.name.toString().localeCompare(b.name.toString(), 'en', { numeric: true }));
        return this.sensorGroups;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSensorGroupsWithSensors(forcedReload: boolean = false): Observable<any> {
    if (this.sensorGroupsWithSensors.length > 0 && !forcedReload) {
      return new Observable<SensorGroup[]>((obs) => {
        obs.next(this.sensorGroupsWithSensors);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading Sensor Groups...');
    return this.http.get(`${ this.apiUrl }/sensorgroup/IncludeSensors`, standardGetHttpOptions).pipe(
      map((response: SensorGroup[]) => {
        this.sensorGroupsWithSensors = response;
        this.sensorGroupsWithSensors = this.sensorGroupsWithSensors.sort((a, b) => a.name.toString().localeCompare(b.name.toString(), 'en', { numeric: true }));
        this.setSensorGroupDates(this.sensorGroupsWithSensors);
        return this.sensorGroupsWithSensors;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateSensorGroup(sensorGroup: SensorGroupInfo, username: string) {
    this.loadingService.setMessage('Updating sensor group...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${ this.apiUrl }/sensorgroup/`, JSON.stringify(sensorGroup), this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public bulkCreateOrUpdateSensorGroups(sensorGroup: SensorGroupInfo[]) {
    this.loadingService.setMessage('Creating Sensor Group...');
    return this.http.post(`${ this.apiUrl }/sensorgroup/bulkCreateOrUpdate`, JSON.stringify(sensorGroup), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public createSensorGroup(sensorGroup: SensorGroupInfo) {
    this.loadingService.setMessage('Creating Sensor Group...');
    return this.http.post(`${ this.apiUrl }/sensorgroup`, JSON.stringify(sensorGroup), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteSensorGroup(sensorGroup: SensorGroup, username: string) {
    this.loadingService.setMessage('Deleting Sensor Group...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/sensorgroup/${ sensorGroup.id }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public searchSensorGroupHistoryByName(recentNames: string[]): Observable<any> {
    this.loadingService.setMessage('Searching Sensor Group History ...');
    return this.http.post(`${ this.apiUrl }/sensorgroup/searchhistory`, JSON.stringify(recentNames), standardHttpOptions).pipe(
      //map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public searchSensorGroupHistoryById(sensorGroupId: number): Observable<any> {
    return this.http.get(`${ this.apiUrl }/sensorgroup/searchhistory/${ sensorGroupId }`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }
  

  public createOrUpdateSensorGroupHistory(sensorGroupHistoryInfo: SensorGroupHistoryInfo): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensorgroup/createOrUpdateHistory`, JSON.stringify(sensorGroupHistoryInfo), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }

  //////////////

  private setSensorGroupDates(sensorGroups: SensorGroup[]) {
    sensorGroups.forEach(x => {
      x.createdDate = moment.utc(x.createdDate).local().toDate();
      x.modifiedDate = moment.utc(x.modifiedDate).local().toDate();
      x.beginDate = moment.utc(x.beginDate).local().toDate();
      x.endDate = moment.utc(x.endDate).local().toDate();

      if (x.plantDate != null) {
        x.plantAge =  -1 * moment.utc(x.plantDate).local().diff(moment(),'days');
      }

      if (x.crop == null || x.crop == '') {
        x.crop = '-';
      }

      if (x.sensors == null) {
        x.sensors = [];
      }

      x.events = [];

      x.sensors.forEach(y => {
        if (y.ignore && y.ignoreUntil != undefined) {
          y.ignoreUntil = moment.utc(y.ignoreUntil).local().toDate();
        }
      });
    });
  }


}
